import React from 'react';
import {
  CalendarOutlined,
  CrownOutlined,
  GiftOutlined,
  ShoppingCartOutlined,
  ThunderboltOutlined,
  UserOutlined
} from '@ant-design/icons';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../i18n/TModules';
import { ConfigStepPanel, TC } from '../configStudio/kit/ConfigPanel';
import SelectionTile, { TileRow } from '../wizard/components/SelectionTile';
import PropertyConditions from '../wizard/kit/PropertyConditions';
import { Checkbox, PickerButton } from '../wizard/kit/controls';
import {
  AppTriggerSelect,
  LoyaltySelect,
  SelectField,
  TextField,
  TransactionTypeField
} from '../wizard/kit/fields';
import { Divider, Form, Hint, Line, Words } from '../wizard/kit/layout';
import { CampaignPicker, RewardPicker } from '../wizard/kit/pickers';
import { CAMPAIGN_ACTIVITIES, CampaignActivity, WizardTrigger } from '../wizard/wizardModel';
import { wz, wzType } from '../wizard/wizardStyles';
import { ruleStepErrors } from './ruleAdapter';
import {
  chooseTrigger,
  CUSTOMER_EVENTS,
  CustomerEvent,
  IRuleStepProps,
  RULE_STEP_TOTAL,
  RULE_TRIGGER_KINDS,
  RuleTriggerKind
} from './ruleModel';

const TR = `${TC}.rule`;
const TT = `${TC}.rule.details`;
const TW = `${TOrbit}.wizard`;

const TRIGGER_ICONS: Record<RuleTriggerKind, React.ReactNode> = {
  transaction: <ShoppingCartOutlined />,
  loyalty: <CrownOutlined />,
  milestone: <CalendarOutlined />,
  reward: <GiftOutlined />,
  customer: <UserOutlined />,
  others: <ThunderboltOutlined />
};

// 6985:7858: four 200-wide tiles 26 apart, then two 240-wide ones, rows 16 apart, all from the
// panel's left edge (the shared row centres its tiles).
const TileRows = styled.div`
  > * {
    justify-content: flex-start;
  }

  > * > * + * {
    margin-left: 26px;
  }

  > * + * {
    margin-top: 16px;
  }
`;

/** 46 Step 1/4: a tile pick starts that trigger and moves on (no Next drawn). */
export const RuleTriggerTypeStep: React.FC<IRuleStepProps> = ({ state, update, back }) => {
  const { t } = useMultiLanguageTranslation();
  const tile = (kind: RuleTriggerKind) => (
    <SelectionTile
      key={kind}
      icon={TRIGGER_ICONS[kind]}
      label={t(`${TR}.trigger.${kind}`)}
      selected={state.triggerKind === kind}
      onSelect={() => update(current => chooseTrigger(current, kind))}
      testId={`orbit-rule-trigger-${kind}`}
    />
  );
  return (
    <ConfigStepPanel
      title={t(`${TR}.title`)}
      step={1}
      total={RULE_STEP_TOTAL}
      question={t(`${TR}.trigger.question`)}
      onNext={null}
      onBack={back}
      testId="orbit-rule-step-trigger"
    >
      <TileRows>
        <TileRow $fixed={200}>{RULE_TRIGGER_KINDS.slice(0, 4).map(tile)}</TileRow>
        <TileRow $fixed={240}>{RULE_TRIGGER_KINDS.slice(4).map(tile)}</TileRow>
      </TileRows>
    </ConfigStepPanel>
  );
};

const SubQuestion = styled.h3`
  margin: 0 0 8px;
  color: ${wz.text};
  font-weight: 700;
  ${wzType.question}
`;

// 6985:7894: the property rows hang off a 1 px rule, 10 in from the checkbox column.
const Indented = styled.div`
  margin-left: 9px;
  padding-left: 20px;
  border-left: 1px solid ${wz.border};
`;

type Patch = (patch: Partial<WizardTrigger>) => void;

/** 47 Step 2/4: the transaction sentence lines, then the drawn narrowing options. */
const TransactionDetails: React.FC<IRuleStepProps & { patch: Patch }> = ({
  state,
  update,
  patch
}) => {
  const { t } = useMultiLanguageTranslation();
  const { trigger } = state;
  return (
    <>
      <Line>
        <Checkbox
          checked={trigger.amountOn}
          label={t(`${TT}.amount`)}
          onChange={amountOn => patch({ amountOn })}
        />
        <Words $muted={!trigger.amountOn}>{t(`${TT}.amount`)}</Words>
        <TextField
          label={t(`${TT}.amount`)}
          placeholder="0.00"
          inputMode="decimal"
          value={trigger.amount}
          disabled={!trigger.amountOn}
          onChange={amount => patch({ amount })}
        />
        <Hint>{t(`${TT}.amountHint`)}</Hint>
      </Line>
      <Line>
        <Checkbox
          checked={trigger.typeOn}
          label={t(`${TT}.type`)}
          onChange={typeOn => patch({ typeOn })}
        />
        <Words $muted={!trigger.typeOn}>{t(`${TT}.type`)}</Words>
        <TransactionTypeField
          value={trigger.transactionType}
          disabled={!trigger.typeOn}
          onChange={transactionType => patch({ transactionType })}
        />
        <Words $muted>{t(`${TT}.typeUnit`)}</Words>
      </Line>
      <Line>
        <Checkbox
          checked={trigger.propertiesOn}
          label={t(`${TT}.properties`)}
          onChange={propertiesOn => patch({ propertiesOn })}
        />
        <Words $muted={!trigger.propertiesOn}>{t(`${TT}.properties`)}</Words>
      </Line>
      <Indented>
        <PropertyConditions
          value={trigger.properties}
          disabled={!trigger.propertiesOn}
          onChange={properties => patch({ properties })}
        />
      </Indented>
      <Divider />
      <SubQuestion>{t(`${TT}.narrow`)}</SubQuestion>
      <Line $dim title={t(`${TT}.merchantsUnsupported`)}>
        <Checkbox
          checked={state.merchantsOn}
          disabled
          label={t(`${TT}.merchants`)}
          onChange={merchantsOn => update(current => ({ ...current, merchantsOn }))}
        />
        <Words $muted>{t(`${TT}.merchants`)}</Words>
        <PickerButton
          icon={<ShoppingCartOutlined />}
          label={t(`${TT}.selectMerchants`)}
          onClick={() => undefined}
          testId="orbit-rule-merchants"
        />
        <Hint>{t(`${TT}.merchantsUnsupported`)}</Hint>
      </Line>
      <Line>
        <Checkbox
          checked={state.firstOnly}
          label={t(`${TT}.firstOnly`)}
          showLabel
          onChange={firstOnly => update(current => ({ ...current, firstOnly }))}
        />
      </Line>
    </>
  );
};

/** Step 2/4 for the other five tiles: not drawn (46 / 47 are reconstructions); module fields. */
const OtherDetails: React.FC<IRuleStepProps & { patch: Patch }> = ({ state, update, patch }) => {
  const { t } = useMultiLanguageTranslation();
  const { trigger } = state;
  switch (state.triggerKind) {
    case 'loyalty':
      return (
        <Line>
          <Words>{t(`${TT}.loyaltyLead`)}</Words>
          <LoyaltySelect value={trigger.loyalty} onChange={loyalty => patch({ loyalty })} />
        </Line>
      );
    case 'milestone':
      return (
        <>
          <Line>
            <Words>{t(`${TT}.milestoneLead`)}</Words>
            <SelectField<CampaignActivity>
              label={t(`${TT}.milestone`)}
              width="xl"
              value={trigger.activity}
              options={CAMPAIGN_ACTIVITIES.map(activity => ({
                value: activity,
                label: t(`${TW}.trigger.milestone.${activity}`)
              }))}
              onChange={activity => patch({ activity })}
            />
          </Line>
          <Line>
            <Words>{t(`${TW}.trigger.milestone.selectCampaign`)}</Words>
            <CampaignPicker
              label={t(`${TW}.trigger.milestone.selectCampaign`)}
              value={trigger.campaign}
              onChange={campaign => patch({ campaign })}
            />
          </Line>
        </>
      );
    case 'reward':
      return (
        <>
          <Line>
            <Words>{t(`${TT}.rewardLead`)}</Words>
            <SelectField<WizardTrigger['rewardActivity']>
              label={t(`${TT}.reward`)}
              width="xl"
              value={trigger.rewardActivity}
              options={[
                { value: 'any', label: t(`${TW}.trigger.reward.any`) },
                { value: 'specific', label: t(`${TW}.trigger.reward.specific`) }
              ]}
              onChange={rewardActivity => patch({ rewardActivity })}
            />
          </Line>
          {trigger.rewardActivity === 'specific' ? (
            <Line>
              <Words>{t(`${TW}.trigger.reward.selected`)}</Words>
              <RewardPicker
                label={t(`${TW}.trigger.reward.selectReward`)}
                value={trigger.reward}
                onChange={reward => patch({ reward })}
              />
            </Line>
          ) : null}
        </>
      );
    case 'customer':
      return (
        <Line>
          <Words>{t(`${TT}.customerLead`)}</Words>
          <SelectField<CustomerEvent>
            label={t(`${TT}.customer`)}
            width="lg"
            value={state.customerEvent}
            options={CUSTOMER_EVENTS.map(event => ({
              value: event,
              label: t(`${TT}.customerEvent.${event}`)
            }))}
            onChange={customerEvent => update(current => ({ ...current, customerEvent }))}
          />
        </Line>
      );
    default:
      return (
        <Line>
          <Words>{t(`${TT}.othersLead`)}</Words>
          <AppTriggerSelect
            value={trigger.appTrigger}
            onChange={appTrigger => patch({ appTrigger })}
          />
        </Line>
      );
  }
};

/** Step 2/4: what about the trigger fires the rule. */
export const RuleTriggerDetailsStep: React.FC<IRuleStepProps> = props => {
  const { state, update, next, back } = props;
  const { t } = useMultiLanguageTranslation();
  const kind = state.triggerKind ?? 'transaction';
  const patch: Patch = change =>
    update(current => ({ ...current, trigger: { ...current.trigger, ...change } }));
  return (
    <ConfigStepPanel
      title={t(`${TR}.title`)}
      step={2}
      total={RULE_STEP_TOTAL}
      question={t(`${TT}.${kind}`)}
      errors={ruleStepErrors(state, t)}
      onNext={next}
      onBack={back}
      testId="orbit-rule-step-triggerDetails"
    >
      <Form>
        {kind === 'transaction' ? (
          <TransactionDetails {...props} patch={patch} />
        ) : (
          <OtherDetails {...props} patch={patch} />
        )}
      </Form>
    </ConfigStepPanel>
  );
};
